/**
 * @file dashboardService.ts
 * @description Typed API client for all 20 dashboard KPI endpoints.
 *
 * ## Interface → KPI Mapping
 * | Interface               | KPIs                              |
 * |-------------------------|-----------------------------------|
 * | GlobalSummary           | KPI 1                             |
 * | AssetStats              | KPI 2, 3, 5                       |
 * | SerializedUnitKpis      | KPI 4                             |
 * | InventoryKpis           | KPI 6, 7, 8                       |
 * | AssignmentKpis          | KPI 9, 10, 11, 12                 |
 * | LicenseStats            | KPI 13, 14, 15                    |
 * | UserStats               | KPI 16                            |
 * | AssetFinancialKpis      | KPI 17, 18, 19                    |
 * | AuditActivityKpis       | KPI 20                            |
 * | DashboardAlerts         | All critical alert counts          |
 *
 * ## Adding a New KPI
 * 1. Add its field(s) to the relevant interface below.
 * 2. If it's from a new endpoint, add a new service method at the bottom.
 * 3. Consume it in DashboardHome.tsx.
 */

import apiClient from './apiClient';

// ─────────────────────────────────────────────
// Shared filter shape — mirrors backend DashboardFilters
// ─────────────────────────────────────────────
export interface DashboardFilters {
  /** ISO code to express monetary aggregates in; defaults to the org DEFAULT_CURRENCY */
  displayCurrency?: string;
  startDate?: string;
  endDate?: string;
  departmentId?: number;
  locationId?: number;
  categoryId?: number;
  brandId?: number;
  vendorId?: number;
  status?: string;
  /** Scopes the kpi-warranty-status drilldown to one tier instead of the combined Expired + ≤90-day view. */
  warrantyBucket?: 'expired' | '30' | '60' | '90';
  /** Scopes the kpi-useful-life drilldown to one tier instead of the combined Near + Beyond view. */
  usefulLifeBucket?: 'near' | 'beyond';
  /** Row count for the "Top Expensive Licenses" list (Story 105); server clamps to a fixed allowed set. */
  topExpensiveLimit?: number;
  /** Scopes a financial-gated license query to this id list — how the Licences tab's cross-filter selection (Story 107) reaches the cost panel, since the fact table it's normally computed from carries no cost fields. */
  licenseIds?: number[];
}

// ─────────────────────────────────────────────
// KPI 1 — Global Summary
// ─────────────────────────────────────────────
export interface GlobalSummary {
  totalAssets: number;
  totalLicenses: number;
  totalInventoryItems: number;
  totalUsers: number;
  /** Sum of asset purchase costs only — no longer includes the (dormant) inventory-value component totalAssetValue used to silently fold in. */
  totalAssetPurchaseCost: number;
  /** Currencies excluded from totalAssetPurchaseCost because no rate is configured for them. */
  missingRateCurrencies: string[];
  /** @deprecated alias for totalAssetPurchaseCost — prefer the explicit name. */
  totalAssetValue: number;
  /** Sum of (months rented × vendorMonthlyRent) across all rented assets, from Rental Start Date to dispose date (or today if still active). */
  totalRentalCostAllTime: number;
  /** Currencies excluded from totalRentalCostAllTime because no rate is configured for them. */
  rentalCostMissingRateCurrencies: string[];
  activeAssignments: number;
}

// ─────────────────────────────────────────────
// KPI drill-down — click-through detail behind a KPI card
// ─────────────────────────────────────────────
/** Item shape varies per KPI (see KPI_DRILLDOWN_CONFIG columns for the fields each KPI actually renders). */
export type KpiDrilldownItem = Record<string, string | number | null> & { id: number | string };

export interface KpiDrilldownResult {
  breakdown: { label: string; value: number }[];
  items: KpiDrilldownItem[];
  total: number;
  page: number;
  pageSize: number;
  /** ISO code the monetary columns (if any) were converted into — set by cases that return cost/spend/value fields */
  displayCurrency?: string;
}

// ─────────────────────────────────────────────
// KPI 2, 3, 5 — Asset Stats
// ─────────────────────────────────────────────
export interface AssetStats {
  byStatus: { status: string; count: string }[];
  byCategory: { category: string; count: string }[];
  // KPI 5 — Warranty tiers
  warrantyExpired: number;
  warrantyExpiring30: number;
  warrantyExpiring60: number;
  warrantyExpiring90: number;
  // KPI 2
  utilizationPercentage: number;
  recentlyAdded: number;
}

// ─────────────────────────────────────────────
// KPI 4 — Serialized Asset Unit Breakdown
// ─────────────────────────────────────────────
export interface SerializedUnitKpis {
  statusBreakdown: { status: string; count: string }[];
  inStock: number;
  assigned: number;
  inRepair: number;
  writtenOff: number;
  disposed: number;
  lost: number;
  total: number;
}

// ─────────────────────────────────────────────
// KPI 6, 7, 8 — Inventory KPIs
// ─────────────────────────────────────────────
export interface InventoryKpis {
  // KPI 6
  belowMinStock: number;
  outOfStock: number;
  // KPI 7
  allTimeSpend: number;
  thisMonthSpend: number;
  lastMonthSpend: number;
  // KPI 8
  turnoverThisMonth: number;
  turnoverLastMonth: number;
  // Legacy (backward compat)
  totalStockUnits: number;
  availableUnits: number;
  lowStockAlerts: number;
  distribution: { refundable: number; nonRefundable: number };
}

// ─────────────────────────────────────────────
// KPI 9, 10, 11, 12 — Assignment KPIs
// ─────────────────────────────────────────────
export interface AssignmentKpis {
  // KPI 9
  active: number;
  serializedActive: number;
  bulkActive: number;
  // KPI 10
  totalOverdue: number;
  overdueImplicit: number;
  // KPI 11
  returnRate: number;
  returnsIn30d: number;
  assignmentsIn30d: number;
  // KPI 12
  damagedReturns: number;
  damagedReturnRate: number;
}

// ─────────────────────────────────────────────
// KPI 13, 14, 15 — License Stats
// ─────────────────────────────────────────────
export interface LicenseStats {
  total: number;
  // KPI 13
  usedSeats: number;
  totalSeats: number;
  availableSeats: number;
  overAllocated: number;
  utilizationPct: number;
  // KPI 14
  expired: number;
  expiring30: number;
  expiring60: number;
  expiring90: number;
  // KPI 15
  annualizedSpend: number;
  spendByFrequency: { frequency: string; total: string }[];
  /** Currencies excluded from annualizedSpend because no rate is configured for them. */
  missingRateCurrencies: string[];
  // Legacy aliases
  assigned: number;
  available: number;
  expiringSoon: number;
  compliancePercentage: number;
}

// ─────────────────────────────────────────────
// KPI 16 — User Stats
// ─────────────────────────────────────────────
export interface UserStats {
  totalUsers: number;
  activeUsers: number;
  usersWithAssets: number;
  assetsPerUser: number;
  mostAssignedDepartment: string;
}

// ─────────────────────────────────────────────
// KPI 17, 18, 19 — Asset Financial KPIs
// ─────────────────────────────────────────────
export interface AssetFinancialKpis {
  // KPI 17
  newThisMonth: number;
  newLastMonth: number;
  newByType: { type: string; count: string }[];
  // KPI 18
  totalBookValue: number;
  totalPurchaseCost: number;
  totalDepreciation: number;
  depreciationPct: number;
  /** Currencies excluded from totalBookValue/totalPurchaseCost/totalDepreciation because no rate is configured for them. */
  missingRateCurrencies: string[];
  // KPI 19
  monthlyRecurringCost: number;
  rentedAssetCount: number;
  /** Currencies excluded from monthlyRecurringCost because no rate is configured for them. */
  mrcMissingRateCurrencies: string[];
  /** Story 104 — book value per department, highest first; unassigned assets grouped as "Unassigned". */
  valueByDepartment: { department: string; value: number }[];
  /** Currencies excluded from any department subtotal for want of a rate. */
  valueByDepartmentMissingRateCurrencies: string[];
}

// ─────────────────────────────────────────────
// KPI 20 — Audit Activity KPIs
// ─────────────────────────────────────────────
export interface AuditActivityKpis {
  total24h: number;
  byAction: { action: string; count: string }[];
  hourlyBreakdown: { hour: string; count: string }[];
  recentEvents: {
    id: number;
    action: string;
    entityType: string;
    entityId: number;
    actorName: string;
    createdAt: string;
    metadata: Record<string, unknown>;
  }[];
  issueCount: number;
  returnCount: number;
  loginCount: number;
  createCount: number;
}

// ─────────────────────────────────────────────
// Aggregated Alerts
// ─────────────────────────────────────────────
export interface DashboardAlerts {
  warrantyExpired: number;
  warrantyExpiring30: number;
  warrantyExpiring60: number;
  lowStockItems: number;
  outOfStock: number;
  expiredLicenses: number;
  licensesExpiring30: number;
  overAllocatedLicenses: number;
  overdueReturns: number;
  damagedReturns: number;
  assetsUnassigned: number;
  totalCritical: number;
}

// ─────────────────────────────────────────────
// Recent Assignment Widgets
// ─────────────────────────────────────────────
export interface RecentAssetAssignment {
  id: number;
  assetName: string;
  assetTag: string;
  assignedTo: string;
  assignedDate: string;
  status: string;
}

export interface RecentLicenseAssignment {
  id: number;
  licenseName: string;
  assignedTo: string;
  expiryDate: string | null;
  status: 'active' | 'expiring_soon' | 'expired';
}

export interface RecentInventoryAssignment {
  id: number;
  itemName: string;
  assignedTo: string;
  quantity: number;
  assignedDate: string;
  status: string;
}

// ─────────────────────────────────────────────
// Phase 3 — Asset/License/Inventory KPIs the Executive-tab foundation left
// open (see docs/ANALYTICS_IMPLEMENTATION_PLAN.md §7.5). `KpiAvailability`
// wraps any KPI that may not be reliably computable (zero denominator, a
// business decision not yet made) — `available: false` means the UI must
// show `reason`, never a fabricated number.
// ─────────────────────────────────────────────
export interface KpiAvailability<T> {
  available: boolean;
  value: T | null;
  reason?: string;
  missingRateCurrencies?: string[];
}

export interface AssetLifecycleKpis {
  byLocation: { site: string; count: string }[];
  nearUsefulLife: number;
  beyondUsefulLife: number;
  disposedThisMonth: number;
  disposalRatePct: KpiAvailability<number>;
  dataCompletenessScore: KpiAvailability<number>;
  missingFieldBreakdown: { field: string; missingCount: number }[];
}

// ─────────────────────────────────────────────
// Story 104 — Asset distribution breakdowns
// ─────────────────────────────────────────────
/** A single bar/slice: one category and how many assets sit in it. */
export interface AssetDistributionBucket {
  label: string;
  count: number;
}

/** A single point on a monthly trend, keyed 'YYYY-MM'. */
export interface AssetTrendPoint {
  month: string;
  count: number;
}

export interface AssetDistributions {
  byDepartment: AssetDistributionBucket[];
  byAssignedUser: AssetDistributionBucket[];
  byBusinessOwner: AssetDistributionBucket[];
  byManufacturer: AssetDistributionBucket[];
  byVendor: AssetDistributionBucket[];
  byModel: AssetDistributionBucket[];
  byAge: AssetDistributionBucket[];
  byWarrantyStatus: AssetDistributionBucket[];
  assignedVsUnassigned: AssetDistributionBucket[];
  byLifecycleStage: AssetDistributionBucket[];
  missingAssets: AssetDistributionBucket[];
  /** Next 12 months of warranty expiries — a forward planning view. */
  warrantyExpiryTrend: AssetTrendPoint[];
  /** Trailing 12 months of acquisitions. */
  purchaseTrend: AssetTrendPoint[];
  repairsStarted: AssetTrendPoint[];
  repairsCompleted: AssetTrendPoint[];
  /** Cap applied to the long-tail charts (user, owner, manufacturer, vendor, model). */
  topN: number;
}

// ─────────────────────────────────────────────
// Story 108 — Assets tab cross-filtering (mirrors LicenseAnalyticsFact)
// ─────────────────────────────────────────────
export interface AssetAnalyticsFact {
  id: number;
  department: string;
  manufacturer: string;
  vendor: string;
  model: string;
  lifecycleStage: string;
  warrantyStatus: string;
  ageBucket: string;
  assignedStatus: 'Assigned' | 'Unassigned';
}

export interface AssetFacts {
  facts: AssetAnalyticsFact[];
  /** True when the scoped fleet exceeds the row cap — facts is empty and cross-filtering is unavailable. */
  factsTruncated: boolean;
}

export interface LicenseCostKpis {
  estimatedUnusedLicenseCost: KpiAvailability<number>;
  costPerAssignedSeat: KpiAvailability<number>;
  costByVendor: { vendor: string; annualizedCost: number; licenseCount: number; missingRateCurrencies: string[] }[];
  complianceRiskExposure: { risk: string; licenseCount: number; annualizedCost: number; missingRateCurrencies: string[] }[];
  /** Story 105 — top N most expensive licenses by total cost (mixed currency, not converted). */
  topExpensive: { softwareName: string; totalCost: number; currency: string }[];
}

// ─────────────────────────────────────────────
// Story 105 — License distribution breakdowns
// ─────────────────────────────────────────────
export interface LicenseDistributionBucket {
  label: string;
  count: number;
}

export interface LicenseTrendPoint {
  month: string;
  count: number;
}

export interface LicenseDistributions {
  bySoftware: LicenseDistributionBucket[];
  byVendor: LicenseDistributionBucket[];
  byType: LicenseDistributionBucket[];
  purchasedVsAssigned: LicenseDistributionBucket[];
  /** Next 12 months of license expiries — a forward planning view. */
  expiryTimeline: LicenseTrendPoint[];
  /** Trailing 12 months of license purchases. */
  purchaseTrend: LicenseTrendPoint[];
  /** Trailing 12 months of recorded renewals. */
  renewalTrend: LicenseTrendPoint[];
  upcomingRenewals: { softwareName: string; vendor: string; nextRenewalDate: string }[];
  overVsUnderLicensed: LicenseDistributionBucket[];
  allocationByUser: LicenseDistributionBucket[];
  expiredByVendor: LicenseDistributionBucket[];
  topN: number;
}

// ─────────────────────────────────────────────
// Story 107 — Licences tab cross-filtering (mirrors CloudAnalyticsFact)
// ─────────────────────────────────────────────
export interface LicenseAnalyticsFact {
  id: number;
  softwareName: string;
  vendor: string;
  type: string;
  status: 'active' | 'expiring' | 'expired';
  statusLabel: string;
  totalSeats: number;
  usedSeats: number;
  purchaseDate: string | null;
  expiryDate: string | null;
}

export interface LicenseFacts {
  facts: LicenseAnalyticsFact[];
  /** True when the scoped register exceeds the row cap — facts is empty and cross-filtering is unavailable. */
  factsTruncated: boolean;
}

export interface InventoryActivityKpis {
  mostFrequentlyIssued: { itemId: number; itemName: string; totalIssued: number; transactionCount: number }[];
  overdueRefundableReturns: number;
  overdueRefundableItems: { id: number; itemName: string; quantity: number; location: string | null; department: string | null; expectedReturnDate: string }[];
  /** Actual date range mostFrequentlyIssued was computed over — the selected filter range, or the last-90-days default when none was selected. */
  period: { startDate: string; endDate: string } | null;
}

// ─────────────────────────────────────────────
// Dashboard Service — all API calls
// ─────────────────────────────────────────────
export const dashboardService = {

  /** KPI 1 — Total counts across all domains */
  async getGlobalSummary(filters?: DashboardFilters, signal?: AbortSignal): Promise<GlobalSummary> {
    const r = await apiClient.get<GlobalSummary>('/api/dashboard/global-summary', { params: filters, signal });
    return r.data;
  },

  /** KPI 2, 3, 5 — Asset utilization, status breakdown, warranty alerts */
  async getAssetStats(filters?: DashboardFilters, signal?: AbortSignal): Promise<AssetStats> {
    const r = await apiClient.get<AssetStats>('/api/dashboard/assets', { params: filters, signal });
    return r.data;
  },

  /** KPI 4 — Serialized asset unit lifecycle breakdown */
  async getSerializedUnitKpis(filters?: DashboardFilters): Promise<SerializedUnitKpis> {
    const r = await apiClient.get<SerializedUnitKpis>('/api/dashboard/serialized-units-kpi', { params: filters });
    return r.data;
  },

  /** KPI 6, 7, 8 — Inventory below-min, purchase spend, turnover */
  async getInventoryKpis(filters?: DashboardFilters, signal?: AbortSignal): Promise<InventoryKpis> {
    const r = await apiClient.get<InventoryKpis>('/api/dashboard/inventory-kpi', { params: filters, signal });
    return r.data;
  },

  /** KPI 9, 10, 11, 12 — Assignments active, overdue, return rate, damage */
  async getAssignmentKpis(filters?: DashboardFilters): Promise<AssignmentKpis> {
    const r = await apiClient.get<AssignmentKpis>('/api/dashboard/assignments-kpi', { params: filters });
    return r.data;
  },

  /** KPI 13, 14, 15 — License seat utilization, expiry tiers, annual spend */
  async getLicenseStats(filters?: DashboardFilters, signal?: AbortSignal): Promise<LicenseStats> {
    const r = await apiClient.get<LicenseStats>('/api/dashboard/licenses', { params: filters, signal });
    return r.data;
  },

  /** KPI 16 — Active users vs. assets ratio */
  async getUserStats(filters?: DashboardFilters): Promise<UserStats> {
    const r = await apiClient.get<UserStats>('/api/dashboard/users', { params: filters });
    return r.data;
  },

  /** KPI 17, 18, 19 — New registrations, book value, rented MRC */
  async getAssetFinancialKpis(filters?: DashboardFilters, signal?: AbortSignal): Promise<AssetFinancialKpis> {
    const r = await apiClient.get<AssetFinancialKpis>('/api/dashboard/asset-financial-kpi', { params: filters, signal });
    return r.data;
  },

  /** KPI 20 — System activity last 24 hours */
  async getAuditActivityKpis(): Promise<AuditActivityKpis> {
    const r = await apiClient.get<AuditActivityKpis>('/api/dashboard/audit-activity-kpi');
    return r.data;
  },

  /** Aggregated critical alert counts for header badge */
  async getAlerts(filters?: DashboardFilters, signal?: AbortSignal): Promise<DashboardAlerts> {
    const r = await apiClient.get<DashboardAlerts>('/api/dashboard/alerts', { params: filters, signal });
    return r.data;
  },

  /** Stock movement bar chart data */
  async getStockMovement(filters?: DashboardFilters, signal?: AbortSignal): Promise<{ thisMonth: { reason: string; count: string }[]; lastMonth: { reason: string; count: string }[] }> {
    const r = await apiClient.get('/api/dashboard/stock-movement', { params: filters, signal });
    return r.data;
  },

  /** Per-software license utilization table */
  async getLicenseUtilization(filters?: DashboardFilters, signal?: AbortSignal): Promise<{
    license_softwareName: string;
    license_totalSeats: number;
    license_usedSeats: number;
    license_expiryDate: string;
    license_totalCost: string;
  }[]> {
    const r = await apiClient.get('/api/dashboard/license-utilization', { params: filters, signal });
    return r.data;
  },

  /** Recent activity feed */
  async getRecentActivity(filters?: DashboardFilters): Promise<unknown[]> {
    const r = await apiClient.get('/api/dashboard/recent-activity', { params: filters });
    return r.data;
  },

  /** KPI card — last 10 asset assignments */
  async getRecentAssetAssignments(limit = 10): Promise<RecentAssetAssignment[]> {
    const r = await apiClient.get<RecentAssetAssignment[]>('/api/dashboard-widgets/recent-assets', { params: { limit } });
    return r.data;
  },

  /** KPI card — last 10 license assignments */
  async getRecentLicenseAssignments(limit = 10): Promise<RecentLicenseAssignment[]> {
    const r = await apiClient.get<RecentLicenseAssignment[]>('/api/dashboard-widgets/recent-licenses', { params: { limit } });
    return r.data;
  },

  /** KPI card — last 10 inventory assignments */
  async getRecentInventoryAssignments(limit = 10): Promise<RecentInventoryAssignment[]> {
    const r = await apiClient.get<RecentInventoryAssignment[]>('/api/dashboard-widgets/recent-inventory', { params: { limit } });
    return r.data;
  },

  /** Click-through detail (breakdown + record list) behind a KPI card */
  async getKpiDrilldown(kpiId: string, params?: DashboardFilters & { page?: number; pageSize?: number }, signal?: AbortSignal): Promise<KpiDrilldownResult> {
    const r = await apiClient.get<KpiDrilldownResult>('/api/dashboard/drilldown', { params: { ...params, kpiId }, signal });
    return r.data;
  },

  /** KPI 14, 20, 24, 26 — asset location breakdown, useful-life pipeline, disposal rate, data completeness */
  async getAssetLifecycleKpis(filters?: DashboardFilters, signal?: AbortSignal): Promise<AssetLifecycleKpis> {
    const r = await apiClient.get<AssetLifecycleKpis>('/api/dashboard/asset-lifecycle-kpi', { params: filters, signal });
    return r.data;
  },

  /** Story 104 — every non-financial "Assets by X" breakdown in one call (analytics.assets.view) */
  async getAssetDistributions(filters?: DashboardFilters, signal?: AbortSignal): Promise<AssetDistributions> {
    const r = await apiClient.get<AssetDistributions>('/api/dashboard/asset-distributions', { params: filters, signal });
    return r.data;
  },

  /** Story 105 — every non-financial "Licenses by X" breakdown in one call (analytics.licenses.view) */
  async getLicenseDistributions(filters?: DashboardFilters, signal?: AbortSignal): Promise<LicenseDistributions> {
    const r = await apiClient.get<LicenseDistributions>('/api/dashboard/license-distributions', { params: filters, signal });
    return r.data;
  },

  /** Story 108 — one fact row per asset, for the Assets tab's click-to-filter cross-filtering */
  async getAssetFacts(filters?: DashboardFilters, signal?: AbortSignal): Promise<AssetFacts> {
    const r = await apiClient.get<AssetFacts>('/api/dashboard/asset-facts', { params: filters, signal });
    return r.data;
  },

  /** Story 107 — one fact row per license, for the Licences tab's click-to-filter cross-filtering */
  async getLicenseFacts(filters?: DashboardFilters, signal?: AbortSignal): Promise<LicenseFacts> {
    const r = await apiClient.get<LicenseFacts>('/api/dashboard/license-facts', { params: filters, signal });
    return r.data;
  },

  /** KPI 35, 36, 39, 40 — unused license cost, cost/seat, cost by vendor, compliance risk exposure */
  async getLicenseCostKpis(filters?: DashboardFilters, signal?: AbortSignal): Promise<LicenseCostKpis> {
    const r = await apiClient.get<LicenseCostKpis>('/api/dashboard/license-cost-kpi', { params: filters, signal });
    return r.data;
  },

  /** KPI 49, 50 — most frequently issued items, overdue refundable returns */
  async getInventoryActivityKpis(filters?: DashboardFilters, signal?: AbortSignal): Promise<InventoryActivityKpis> {
    const r = await apiClient.get<InventoryActivityKpis>('/api/dashboard/inventory-activity-kpi', { params: filters, signal });
    return r.data;
  },
};
